// src/App.tsx
import React, { lazy, Suspense } from "react";
import {
    Navigate,
    Route,
    BrowserRouter as Router,
    Routes,
} from "react-router-dom";
import PageLoader from "./components/PageLoader";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { EventProvider } from "./context/EventContext";
import Layout from "./Layout";
import Landing from "./pages/Landing";

// Everything except the landing page is split into its own chunk, so
// first-time visitors never download the admin area or the auth forms.
const AdminEventDetails = lazy(() => import("./pages/AdminEventDetails"));
const AdminHome = lazy(() => import("./pages/AdminHome"));
const Auth = lazy(() => import("./pages/Auth"));
const Events = lazy(() => import("./pages/Events"));
const EventSubscription = lazy(() => import("./pages/EventSubscription"));
const TermsAndConditions = lazy(() => import("./pages/TermsAndConditions"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));

const ProtectedRoute: React.FC<{
  element: React.ReactElement;
  adminOnly?: boolean;
}> = ({ element, adminOnly = false }) => {
  const { user, isAdmin, loading } = useAuth();

  if (loading) return <PageLoader />;
  if (!user) return <Navigate to="/auth" replace />;
  if (adminOnly && !isAdmin) return <Navigate to="/" replace />;

  return element;
};

export const App: React.FC = () => {
  return (
    <Router>
      <AuthProvider>
        <EventProvider>
          <Layout>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<Landing />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/terms" element={<TermsAndConditions />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route
                  path="/events"
                  element={<ProtectedRoute element={<Events />} />}
                />
                <Route
                  path="/event/:eventId"
                  element={<ProtectedRoute element={<EventSubscription />} />}
                />
                <Route
                  path="/admin"
                  element={
                    <ProtectedRoute element={<AdminHome />} adminOnly={true} />
                  }
                />
                <Route
                  path="/admin/event/:eventId"
                  element={
                    <ProtectedRoute
                      element={<AdminEventDetails />}
                      adminOnly={true}
                    />
                  }
                />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
          </Layout>
        </EventProvider>
      </AuthProvider>
    </Router>
  );
};

export default App;
